import React, { useState, useCallback } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { Menu } from "react-native-paper";
import { useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import { GestureResponderEvent } from "react-native";
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";

import CustomText from "../common/general/CustomText/CustomText";
import type { RootStackParamList } from "../../navigation/navigation.types";

interface Student {
  id: string;
  name: string;
  email: string;
  pendingDegreeEnrollmentInvite?: boolean;
  enrollmentDate: string;
}

interface AdminDegreeRosterManagementTableProps {
  students: Student[];
  onResendInvite: (studentName: string, studentEmail: string) => void;
  handleRemoveStudents: (studentId: string) => void;
}

type MenuState = {
  visible: boolean;
  x: number;
  y: number;
  studentId: string;
  studentName: string;
  studentEmail: string;
  expiredInvitation: boolean;
  pendingDegreeEnrollmentInvite: boolean;
};

const AdminDegreeRosterManagementTable: React.FC<AdminDegreeRosterManagementTableProps> = ({
  students,
  onResendInvite,
  handleRemoveStudents,
}) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    x: 0,
    y: 0,
    studentId: "",
    studentName: "",
    studentEmail: "",
    expiredInvitation: false,
    pendingDegreeEnrollmentInvite: false,
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleOpenMenu = useCallback((event: GestureResponderEvent, student: Student) => {
    const { pageX, pageY } = event.nativeEvent;
    setMenuState({
      visible: true,
      x: pageX - 100,
      y: pageY,
      studentId: student.id,
      studentName: student.name,
      studentEmail: student.email,
      expiredInvitation: false,
      pendingDegreeEnrollmentInvite: student.pendingDegreeEnrollmentInvite,
    });
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuState({
      visible: false,
      x: 0,
      y: 0,
      studentId: "",
      studentName: "",
      studentEmail: "",
      expiredInvitation: false,
      pendingDegreeEnrollmentInvite: false,
    });
  }, []);

  const handleViewProfile = () => {
    handleCloseMenu();
    navigation.navigate("Student Profile", { studentId: menuState.studentId });
  };

  const handleResendInvite = async (studentName: string, studentEmail: string) => {
    handleCloseMenu();
    onResendInvite(studentName, studentEmail);
  };

  return (
    <>
      <View style={styles.tableContainer}>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.studentCell]}>
              <CustomText text="Student" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.emailCell]}>
              <CustomText text="Email" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.enrollmentDateCell]}>
              <CustomText text="Enrollment Date" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
            <View style={[styles.cell, styles.statusCell]}>
              <CustomText text="Status" size="s" weight="medium" style={{}} />
            </View>
            <View style={[styles.cell, styles.actionsCell]}>
              <CustomText text="Actions" weight="bold" style={styles.headerText} useTranslationText={false} />
            </View>
          </View>

          {/* Table Body */}
          {students.map((student) => {
            const isMenuOpen = menuState.visible && menuState.studentId === student.id;
            const initials = student.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .substring(0, 2);

            return (
              <View key={student.id} style={styles.tableRow}>
                <View style={[styles.cell, styles.studentCell]}>
                  <View style={styles.studentInfo}>
                    <View style={styles.avatar}>
                      <CustomText text={initials} weight="bold" style={styles.avatarText} useTranslationText={false} />
                    </View>
                    <CustomText
                      text={student.name}
                      size="s"
                      weight="medium"
                      style={styles.studentName}
                      useTranslationText={false}
                    />
                  </View>
                </View>

                <View style={[styles.cell, styles.emailCell]}>
                  <CustomText text={student.email} size="s" style={styles.emailText} useTranslationText={false} />
                </View>

                <View style={[styles.cell, styles.enrollmentDateCell]}>
                  <CustomText
                    text={formatDate(student.enrollmentDate)}
                    size="s"
                    style={styles.dateText}
                    useTranslationText={false}
                  />
                </View>

                <View style={[styles.cell, styles.statusCell]}>
                  <CustomText
                    text={student.pendingDegreeEnrollmentInvite ? "Invited" : "Enrolled"}
                    size="s"
                    style={[
                      styles.statusText,
                      student.pendingDegreeEnrollmentInvite ? styles.invitedText : styles.enrolledText,
                    ]}
                    useTranslationText={false}
                  />
                </View>

                <View style={[styles.cell, styles.actionsCell]}>
                  <Pressable
                    style={[styles.manageButton, isMenuOpen && styles.manageButtonActive]}
                    onPress={(e) => handleOpenMenu(e, student)}>
                    <CustomText
                      text="Manage"
                      size="s"
                      style={[styles.manageText, isMenuOpen && styles.manageTextActive]}
                      useTranslationText={false}
                    />
                    {isMenuOpen ? (
                      <CaretUpIcon size={14} color="#FFFFFF" />
                    ) : (
                      <CaretDownIcon size={14} color="#475569" />
                    )}
                  </Pressable>
                </View>
              </View>
            );
          })}
        </View>
      </View>

      <Menu
        visible={menuState.visible}
        onDismiss={handleCloseMenu}
        anchor={{ x: menuState.x, y: menuState.y }}
        contentStyle={styles.menu}>
        <Menu.Item onPress={handleViewProfile} title="View Profile" titleStyle={styles.menuItemText} />
        <Menu.Item
          onPress={() => handleResendInvite(menuState.studentName, menuState.studentEmail)}
          title="Resend Invite"
          titleStyle={[
            styles.menuItemText,
            !menuState.expiredInvitation && !menuState.pendingDegreeEnrollmentInvite && styles.disabledText,
          ]}
          disabled={!menuState.expiredInvitation && !menuState.pendingDegreeEnrollmentInvite}
        />
        <View style={styles.menuDivider} />
        <Menu.Item
          onPress={() => {
            handleRemoveStudents(menuState.studentId);
          }}
          title="Remove from Degree Program"
          titleStyle={[styles.menuItemText, styles.deleteText]}
        />
      </Menu>
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  studentCell: {
    flex: 3,
  },
  emailCell: {
    flex: 3,
  },
  enrollmentDateCell: {
    flex: 2,
  },
  statusCell: {
    flex: 2,
    minHeight: 48,
  },
  actionsCell: {
    flex: 1.25,
    alignItems: "center",
  },
  headerText: {
    color: "#475569",
    fontSize: 14,
    letterSpacing: 0.5,
  },
  studentInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 16,
  },
  studentName: {
    color: "#101828",
  },
  emailText: {
    color: "#101828",
  },
  dateText: {
    color: "#101828",
  },
  manageButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
  },
  manageButtonActive: {
    backgroundColor: "#3E68FE",
    borderColor: "#3E68FE",
  },
  manageText: {
    color: "#475569",
    fontSize: 13,
  },
  manageTextActive: {
    color: "#FFFFFF",
  },
  menu: {
    backgroundColor: "#FFFFFF",
    minWidth: 200,
  },
  menuItemText: {
    fontSize: 14,
    color: "#374151",
  },
  deleteText: {
    color: "#DC2626",
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 4,
  },
  disabledText: {
    color: "#9CA3AF",
  },
  statusText: {
    fontSize: 14,
    fontWeight: "500",
  },
  invitedText: {
    color: "#F59E0B",
  },
  enrolledText: {
    color: "#10B981",
  },
});

export default AdminDegreeRosterManagementTable;
